import { useCallback, useRef, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const PAIRS = [
  {
    id: "airmax",
    before: "/gallery/airmax-before.jpg",
    after: "/gallery/airmax-after.jpg",
    caption: "Nike Air Max 97",
    service: "Deep Clean",
  },
  {
    id: "converse",
    before: "/gallery/converse-before.jpg",
    after: "/gallery/converse-after.jpg",
    caption: "Converse high-tops",
    service: "Full Revival",
  },
  {
    id: "kids",
    still: true,
    img: "/gallery/kids-comparison.png",
    caption: "Kids' school trainers — one revived, one next in line",
    service: "The Refresh",
  },
  {
    id: "jordan",
    before: "/gallery/jordan-before.jpg",
    after: "/gallery/jordan-after.jpg",
    caption: "Jordan 1 — fresh ribbon laces",
    service: "Full Revival",
  },
  {
    id: "newbalance",
    before: "/gallery/nb-before.png",
    after: "/gallery/nb-after.png",
    caption: "New Balance 327",
    service: "Deep Clean",
  },
  {
    id: "nbxc",
    before: "/gallery/nbxc-before.jpg",
    after: "/gallery/nbxc-after.jpg",
    caption: "New Balance XC-72",
    service: "Full Revival",
  },
  {
    id: "oncloud",
    before: "/gallery/oncloud-before.png",
    after: "/gallery/oncloud-after.png",
    caption: "On Cloud runner",
    service: "Deep Clean",
  },
  {
    id: "creamhitop",
    before: "/gallery/creamhitop-before.jpg",
    after: "/gallery/creamhitop-after.jpg",
    caption: "Cream leather high-tops",
    service: "Full Revival",
  },
  {
    id: "arne",
    before: "/gallery/arne-before.jpg",
    after: "/gallery/arne-after.jpg",
    caption: "ARNE low-tops — laces out, deep clean in",
    service: "Deep Clean",
  },
];

const BeforeAfterSlider = ({ pair }) => {
  const ref = useRef(null);
  const dragging = useRef(false);
  const [pos, setPos] = useState(50);
  const [touched, setTouched] = useState(false);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const p = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(95, Math.max(5, p)));
  }, []);

  return (
    <div
      ref={ref}
      data-testid={`before-after-${pair.id}`}
      className="relative aspect-[4/5] cursor-ew-resize touch-none select-none overflow-hidden border border-white/10"
      onPointerDown={(e) => {
        dragging.current = true;
        setTouched(true);
        e.currentTarget.setPointerCapture(e.pointerId);
        update(e.clientX);
      }}
      onPointerMove={(e) => dragging.current && update(e.clientX)}
      onPointerUp={() => (dragging.current = false)}
      onPointerCancel={() => (dragging.current = false)}
    >
      <img
        src={pair.before || pair.img}
        alt={`${pair.caption} before cleaning`}
        draggable={false}
        className={`absolute inset-0 h-full w-full object-cover ${
          pair.before ? "" : "brightness-[0.45] contrast-125 grayscale-[0.85]"
        }`}
      />
      <div className="absolute inset-0" style={{ clipPath: `inset(0 0 0 ${pos}%)` }}>
        <img
          src={pair.after || pair.img}
          alt={`${pair.caption} after cleaning`}
          draggable={false}
          className="h-full w-full object-cover"
        />
      </div>

      <span className="absolute left-4 top-4 bg-black/60 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-300 backdrop-blur-sm">
        Before
      </span>
      <span className="absolute right-4 top-4 bg-aqua px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-obsidian">
        After
      </span>

      <div className="absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-aqua shadow-[0_0_16px_rgba(0,229,255,0.8)]" />
        <div className="absolute top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-obsidian bg-aqua text-obsidian shadow-[0_0_24px_rgba(0,229,255,0.7)]">
          <MoveHorizontal size={18} />
        </div>
      </div>

      {!touched && (
        <span className="absolute bottom-4 left-1/2 -translate-x-1/2 animate-pulse bg-black/60 px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-aqua backdrop-blur-sm">
          Drag to reveal
        </span>
      )}
    </div>
  );
};

const StillCard = ({ pair }) => (
  <div data-testid={`before-after-${pair.id}`} className="relative aspect-[4/5] overflow-hidden border border-white/10">
    <img src={pair.img} alt={pair.caption} draggable={false} className="h-full w-full object-cover" />
    <span className="absolute left-4 top-4 bg-aqua px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.25em] text-obsidian">
      One cleaned — one waiting
    </span>
  </div>
);

export default function Gallery() {
  return (
    <section id="results" data-testid="results-section" className="border-t border-white/5 bg-carbon/40">
      <div className="mx-auto max-w-7xl px-6 py-28 lg:px-12 lg:py-40">
        <Reveal className="flex flex-wrap items-end justify-between gap-8">
          <div>
            <Overline>The evidence</Overline>
            <h2 className="mt-6 font-display text-4xl font-extrabold uppercase leading-[1.02] tracking-tighter text-white sm:text-5xl lg:text-6xl">
              Drag the line.
            </h2>
          </div>
          <p className="max-w-sm text-sm leading-relaxed text-zinc-400">
            Real pairs, real results. Pull the handle across each photo to see the transformation.
          </p>
        </Reveal>

        <div className="mt-16 grid gap-8 md:grid-cols-2 lg:grid-cols-3 lg:gap-10">
          {PAIRS.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.12}>
              {p.still ? <StillCard pair={p} /> : <BeforeAfterSlider pair={p} />}
              <div className="mt-4 flex items-center justify-between gap-4">
                <p className="text-sm text-zinc-300">{p.caption}</p>
                <p className="shrink-0 font-mono text-[10px] uppercase tracking-[0.2em] text-aqua">{p.service}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

